/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Bans sub-tab — list/search/issue/remove + import + sync.
 *
 * Issue ban form supports: player_id (required), reason (required),
 * optional server (cross-server when blank), optional duration in days
 * (blank = permanent). Remove is a single-click action with a confirm.
 */
import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Ban, Plus, Trash2, RefreshCw, Search, Download, Upload, AlertTriangle } from "lucide-react";

export default function BansTab({ toast }) {
  const [bans, setBans] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [servers, setServers] = useState([]);
  const [form, setForm] = useState({ player_id: "", player_name: "", reason: "", server: "", days: "" });
  const [importServer, setImportServer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Per-server banlist.txt / RCON failures from the last ban or unban.
  // These used to be swallowed into the log file, so an admin saw "Banned ✅"
  // even when the ban wouldn't survive a server restart.
  const [syncWarnings, setSyncWarnings] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/moderation/bans", { params: q ? { q } : {} });
      setBans(data.bans || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, toast]);

  useEffect(() => {
    load();
    api.get("/admin/moderation/servers").then(({ data }) => setServers(data.servers || []));
  }, [load]);

  const issueBan = async (e) => {
    e?.preventDefault();
    if (!form.player_id.trim() || !form.reason.trim()) {
      toast.error("Player ID and reason are required");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        player_id: form.player_id.trim(),
        reason: form.reason.trim(),
        ...(form.player_name.trim() ? { player_name: form.player_name.trim() } : {}),
        ...(form.server ? { server: form.server } : {}),
        ...(form.days ? { days: parseInt(form.days, 10) } : {}),
      };
      const { data } = await api.post("/admin/moderation/bans", body);
      const ids = data.identity || {};
      const extra = [
        ids.discord_ids?.length ? `${ids.discord_ids.length} Discord id(s)` : null,
        ids.player_userids?.length ? `${ids.player_userids.length} character(s)` : null,
      ].filter(Boolean).join(", ");
      toast.success(
        `Banned ${form.player_id} (synced to ${data.synced_to.length} server${data.synced_to.length === 1 ? "" : "s"})`
        + (extra ? ` — recorded against ${extra}` : "")
      );
      if (data.unified_bans_available === false) {
        toast.error("Shared ban record NOT written: the unified_bans table doesn't exist yet. Start the updated bot once, then run Import to shared record.");
      }
      const warns = data.warnings || [];
      setSyncWarnings(warns);
      warns.forEach((w) => toast.error(w));
      setForm({ player_id: "", player_name: "", reason: "", server: "", days: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const removeBan = async (player_id) => {
    if (!window.confirm(`Remove ban for ${player_id}? This lifts the shared ban record and unbans them on every connected server.\n\nDiscord unban is SEPARATE — the bot handles that.`)) return;
    try {
      const { data } = await api.delete(`/admin/moderation/bans/${encodeURIComponent(player_id)}`);
      toast.success(`Unbanned ${player_id}${data?.unified_rows_lifted ? ` — ${data.unified_rows_lifted} shared record(s) lifted` : ""}. Discord unban is separate.`);
      const warns = data?.warnings || [];
      setSyncWarnings(warns);
      warns.forEach((w) => toast.error(w));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const importUnified = async () => {
    try {
      const { data } = await api.post("/admin/moderation/bans/import-unified");
      toast.success(`Backfilled ${data.rows_added} shared ban row(s) from ${data.candidates} record(s); ${data.already_banned} already covered`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const importBanlist = async () => {
    if (!importServer) { toast.error("Pick a server first"); return; }
    try {
      const { data } = await api.post(`/admin/moderation/bans/import?server=${encodeURIComponent(importServer)}`);
      toast.success(`Imported ${data.added} new ban${data.added === 1 ? "" : "s"} (found ${data.found})`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const syncAll = async () => {
    if (!window.confirm("Push every active ban to every server's banlist.txt? This may overwrite existing entries.")) return;
    try {
      const { data } = await api.post("/admin/moderation/bans/sync");
      const ok = Object.entries(data.results || {}).filter(([, v]) => v === "ok").length;
      const tot = Object.keys(data.results || {}).length;
      toast.success(`Synced ${data.count} bans to ${ok}/${tot} servers`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      {syncWarnings.length > 0 && (
        <div
          className="glass-card p-4 border border-[var(--danger,#ff5570)]/50 bg-[rgba(255,85,112,0.06)] space-y-2"
          data-testid="ban-sync-warnings"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="label flex items-center gap-2 text-[var(--danger,#ff5570)]">
              <AlertTriangle size={14} /> Ban sync did not fully succeed
            </h3>
            <button
              onClick={() => setSyncWarnings([])}
              className="btn btn-sm btn-secondary"
              data-testid="ban-sync-warnings-dismiss"
            >
              Dismiss
            </button>
          </div>
          <ul className="text-xs space-y-1 list-disc pl-5">
            {syncWarnings.map((w) => (
              <li key={w} data-testid="ban-sync-warning-row">{w}</li>
            ))}
          </ul>
          <p className="text-[11px] text-[var(--muted)]">
            Fix the SFTP/RCON connection for the listed server(s), then use “Sync all to banlist.txt” to re-push.
          </p>
        </div>
      )}

      {/* Issue ban */}
      <form onSubmit={issueBan} className="glass-card p-4 space-y-3" data-testid="ban-issue-form">
        <h3 className="label flex items-center gap-2"><Plus size={14} /> Issue a Ban</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            value={form.player_id}
            onChange={(e) => setForm({ ...form, player_id: e.target.value })}
            placeholder="Player ID (e.g. steam_76561198…)"
            className="input"
            data-testid="ban-player-id"
            required
          />
          <input
            value={form.player_name}
            onChange={(e) => setForm({ ...form, player_name: e.target.value })}
            placeholder="Player name (optional)"
            className="input"
            data-testid="ban-player-name"
          />
        </div>
        <input
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder="Reason (visible to player on appeal)"
          className="input"
          data-testid="ban-reason"
          required
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <select
            value={form.server}
            onChange={(e) => setForm({ ...form, server: e.target.value })}
            className="input"
            data-testid="ban-server"
          >
            <option value="">All servers</option>
            {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
          <input
            type="number"
            min="0"
            value={form.days}
            onChange={(e) => setForm({ ...form, days: e.target.value })}
            placeholder="Duration in days (blank = permanent)"
            className="input"
            data-testid="ban-days"
          />
        </div>
        <button type="submit" disabled={submitting} className="btn btn-rose" data-testid="ban-submit">
          <Ban size={14} /> {submitting ? "Banning…" : "Issue Ban"}
        </button>
        <p className="text-[11px] text-[var(--muted)]" data-testid="ban-scope-note">
          The ban is recorded against every identifier this player is known by — Discord account,
          website account, character UId and platform id — and written to each server's
          <span className="font-mono"> banlist.txt</span>. <strong>Discord ban/unban is separate</strong> and
          handled by the bot.
        </p>
      </form>

      {/* Import + sync */}
      <div className="glass-card p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <select value={importServer} onChange={(e) => setImportServer(e.target.value)} className="input flex-1" data-testid="ban-import-server">
          <option value="">Pick a server to import banlist.txt from</option>
          {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
        <button onClick={importBanlist} className="btn" data-testid="ban-import-btn"><Download size={14} /> Import</button>
        <button onClick={importUnified} className="btn" data-testid="ban-import-unified-btn"><Download size={14} /> Import to shared record</button>
        <button onClick={syncAll} className="btn btn-teal" data-testid="ban-sync-btn"><Upload size={14} /> Sync all to servers</button>
      </div>

      {/* Search */}
      <div className="glass-card p-3 flex gap-2 items-center">
        <Search size={14} className="text-[var(--muted)]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="Search by player ID, name, or reason"
          className="input flex-1"
          data-testid="ban-search"
        />
        <button onClick={load} className="btn btn-ghost" data-testid="ban-refresh"><RefreshCw size={14} /></button>
      </div>

      {/* List */}
      <div className="glass-card p-4" data-testid="ban-list">
        {loading ? (
          <p className="text-[var(--muted)] text-sm">Loading…</p>
        ) : bans.length === 0 ? (
          <p className="text-[var(--muted)] text-sm">No bans match.</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {bans.map((b) => (
              <li key={b.id} className="py-3 flex items-start gap-3" data-testid={`ban-row-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[var(--bone)] truncate" title={b.player_id}>{b.player_name || b.player_id}</span>
                    {b.auto_ban ? (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300">Auto</span>
                    ) : null}
                    {b.expires_at ? (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-teal-500/20 text-teal-300" title={b.expires_at}>Temp</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-[var(--muted)] mt-0.5">
                    <span className="font-mono">{b.player_id}</span>
                    {b.server ? <> · {b.server}</> : null}
                    {b.banned_at ? <> · {b.banned_at}</> : null}
                  </div>
                  <p className="text-sm text-white/85 mt-1">{b.reason}</p>
                </div>
                <button
                  onClick={() => removeBan(b.player_id)}
                  className="btn btn-ghost text-rose-300"
                  data-testid={`ban-remove-${b.id}`}
                  title="Unban"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
